import {
  type OrganisationT,
  type OrganisationPartnerWithStatusT,
  type OrganisationWithIntegrationsT,
} from '@ruminati/types_v2/organisation'
import { type OrganisationMemberT } from '@ruminati/types_v2/organisation_member'
import { type PendingOrgInvitationT, type UserT } from '@ruminati/types_v2/user'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, nextTick, ref, watch } from 'vue'
import { isNavigationFailure, NavigationFailureType, useRouter } from 'vue-router'

import { queryClient } from '@/services/queryClient'
import {
  sendBroadcastMessageFromTabIfFocused,
  SESSION_CHANGE_REASONS,
} from '@/utilities/broadcaster'
import { getEnvFromUrl } from '@/utilities/env'

import { useUiStore } from './ui'

export const useOrganisationStore = defineStore('organisation', () => {
  const router = useRouter()

  // N.B. organisations are not fetched directly — they come back as part of the profile fetch
  const organisations = ref<Array<OrganisationWithIntegrationsT> | undefined>()
  const pendingOrgInvitations = ref<Array<PendingOrgInvitationT>>([])
  const currentOrgId = useStorage<string | undefined>(
    `ruminati_${getEnvFromUrl()}_current_org_id`,
    undefined,
  )

  function setOrganisations(newOrganisations: OrganisationWithIntegrationsT[]) {
    if (organisations.value) organisations.value.length = 0
    else if (organisations.value === undefined) organisations.value = []
    organisations.value.push(...newOrganisations)
  }

  function upsertOrganisations(newOrganisations: Array<OrganisationT>) {
    if (organisations.value === undefined) {
      organisations.value = newOrganisations
    } else {
      const existingMap = new Map(organisations.value.map((o) => [o.id, o]))
      for (const newOrg of newOrganisations) {
        existingMap.set(newOrg.id, newOrg)
      }
      organisations.value = Array.from(existingMap.values())
    }
  }

  function setPendingOrgInvitations(newPendingOrgInvitations: Array<PendingOrgInvitationT>) {
    pendingOrgInvitations.value = newPendingOrgInvitations
  }

  function removePendingOrgInvitation(relationshipId: string) {
    pendingOrgInvitations.value = pendingOrgInvitations.value.filter(
      (invitation) => invitation.relationship.id !== relationshipId,
    )
  }

  function reset() {
    currentOrgId.value = undefined
    organisations.value = undefined
    pendingOrgInvitations.value = []
  }

  function initializeCurrentOrg(profile?: UserT) {
    if (!organisations.value || organisations.value.length === 0) return
    if (currentOrgId.value && organisations.value.find((o) => o.id === currentOrgId.value)) return

    const primaryOrgId = profile?.userPreferences?.primaryOrganisationId
    if (primaryOrgId && organisations.value.find((o) => o.id === primaryOrgId)) {
      currentOrgId.value = primaryOrgId
      return
    }

    currentOrgId.value = organisations.value[0].id
  }

  const currentOrg = computed(() => {
    if (!currentOrgId.value || !organisations.value) return undefined
    return organisations.value.find((o) => o.id === currentOrgId.value)
  })

  async function changeCurrentOrg(newOrgId: string): Promise<void> {
    if (newOrgId === currentOrgId.value) return

    const uiStore = useUiStore()
    if (uiStore.isSessionTransitioning) return
    uiStore.startSessionTransition('changing-organisation')

    // Allow the root RouterView and all of its query observers to unmount before clearing query cache (otherwise current observers are triggered again to make fetches with the current org context, not the new org context).
    await nextTick()

    try {
      await queryClient.cancelQueries()
      queryClient.clear()

      // Navigate while the old context is still internally consistent. The RouterView remains
      // unmounted until the new organisation and its scoped stores are ready.
      const navigationFailure = await router.push({ name: 'home' })
      if (
        isNavigationFailure(navigationFailure) &&
        !isNavigationFailure(navigationFailure, NavigationFailureType.duplicated)
      ) {
        throw navigationFailure
      }

      currentOrgId.value = newOrgId
      uiStore.reset()

      // Allow org-scoped store watchers to reset before the new route mounts.
      await nextTick()

      sendBroadcastMessageFromTabIfFocused(SESSION_CHANGE_REASONS.ORG_CHANGED)
    } finally {
      uiStore.finishSessionTransition()
    }
  }

  const currentOrganisationIsPrime = computed(() => true)

  return {
    organisations,
    pendingOrgInvitations,
    currentOrgId,
    setOrganisations,
    setPendingOrgInvitations,
    removePendingOrgInvitation,
    currentOrganisationIsPrime,
    upsertOrganisations,

    reset,
    initializeCurrentOrg,
    currentOrg,
    changeCurrentOrg,
  }
})

export const usePartnersStore = defineStore('partners', () => {
  const organisationStore = useOrganisationStore()

  const partners = ref<Array<OrganisationPartnerWithStatusT> | undefined>()

  function setPartners(newPartners: Array<OrganisationPartnerWithStatusT>) {
    partners.value = newPartners
  }

  function reset() {
    partners.value = undefined
  }

  watch(
    () => organisationStore.currentOrgId,
    () => reset(),
  )

  return {
    partners,
    setPartners,
    reset,
  }
})

export const useOrgMembersStore = defineStore('members', () => {
  const organisationStore = useOrganisationStore()

  const orgMembers = ref<Array<OrganisationMemberT> | undefined>()

  function setOrgMembers(newMembers: Array<OrganisationMemberT>) {
    orgMembers.value = newMembers
  }

  function reset() {
    orgMembers.value = undefined
  }

  const orgUserMembers = computed(() => {
    return (orgMembers.value ?? []).filter((m) => m.relationship.recipientType === 'User')
  })

  const orgUserMembersWithSamePrimaryOrgId = computed(() => {
    return (orgMembers.value ?? []).filter(
      (m) =>
        m.relationship.recipientType === 'User' &&
        m.recipient?.primaryOrganisationId === organisationStore.currentOrg?.id,
    )
  })

  const orgUserMembersWithDiffPrimaryOrg = computed(() => {
    return (orgMembers.value ?? []).filter(
      (m) =>
        m.relationship.recipientType === 'User' &&
        m.recipient?.primaryOrganisationId !== organisationStore.currentOrg?.id,
    )
  })

  const orgActiveUserMembers = computed(() => {
    return orgUserMembers.value.filter((m) => m.relationship.activatedDate)
  })

  const orgPendingUserMembers = computed(() => {
    return (orgMembers.value ?? []).filter((m) => {
      const isUser = m.relationship.recipientType === 'User'
      const hasJoined = !m.relationship.activatedDate
      let hasDiffPrimaryOrg = false
      if (m.recipient?.primaryOrganisationId) {
        hasDiffPrimaryOrg = m.recipient.primaryOrganisationId !== organisationStore.currentOrg?.id
      }
      return isUser && hasJoined && !hasDiffPrimaryOrg
    })
  })

  const orgActiveUserMembersAsOptions = computed(() => {
    return [
      { value: 'all', label: 'Any' },
      ...orgActiveUserMembers.value.map((u) => ({
        value: u.recipient?.id,
        label: `${u.recipient?.firstName} ${u.recipient?.lastName}`,
      })),
    ]
  })

  watch(
    () => organisationStore.currentOrgId,
    () => reset(),
  )

  return {
    orgMembers,
    setOrgMembers,
    reset,

    orgUserMembers,
    orgUserMembersWithSamePrimaryOrgId,
    orgActiveUserMembers,
    orgActiveUserMembersAsOptions,
    orgPendingUserMembers,
    orgUserMembersWithDiffPrimaryOrg,
  }
})
